import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Column {
  id: string;
  date: string;
  title: string;
  content: string;
  author: string;
  category: string;
  target: string;
  createdAt: number;
  imageUrl?: string;
}

interface TabConfig {
  id: string;
  name: string;
  type: 'author' | 'category' | 'target';
}

const defaultAuthorTabs: TabConfig[] = [
  { id: 'doctor', name: '医師', type: 'author' },
  { id: 'nurse', name: '看護師', type: 'author' },
  { id: 'pt', name: '理学療法士', type: 'author' },
  { id: 'nutritionist', name: '管理栄養士', type: 'author' },
  { id: 'other-author', name: 'その他', type: 'author' }
];

const defaultCategoryTabs: TabConfig[] = [
  { id: 'exercise', name: '運動指導', type: 'category' },
  { id: 'nutrition', name: '栄養指導', type: 'category' },
  { id: 'lifestyle', name: '生活指導', type: 'category' },
  { id: 'other-category', name: 'その他', type: 'category' }
];

const defaultTargetTabs: TabConfig[] = [
  { id: 'patient', name: '患者さんへ', type: 'target' },
  { id: 'medical', name: '医療従事者の方へ', type: 'target' },
  { id: 'other-target', name: 'その他', type: 'target' }
];

export default function Column() {
  const [columns, setColumns] = useState<Column[]>([]);
  const [selectedColumn, setSelectedColumn] = useState<Column | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [authorTabs, setAuthorTabs] = useState<TabConfig[]>(defaultAuthorTabs);
  const [categoryTabs, setCategoryTabs] = useState<TabConfig[]>(defaultCategoryTabs);
  const [targetTabs, setTargetTabs] = useState<TabConfig[]>(defaultTargetTabs);
  const [activeAuthorTab, setActiveAuthorTab] = useState('all');
  const [activeCategoryTab, setActiveCategoryTab] = useState('all');
  const [activeTargetTab, setActiveTargetTab] = useState('all');
  const [showTabManager, setShowTabManager] = useState(false);
  const [editingColumn, setEditingColumn] = useState<Column | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  
  const [formData, setFormData] = useState({
    date: '',
    title: '',
    content: '',
    author: 'doctor',
    category: 'exercise',
    target: 'patient'
  });

  const [newTabName, setNewTabName] = useState('');
  const [newTabType, setNewTabType] = useState<'author' | 'category' | 'target'>('author');

  useEffect(() => {
    loadColumns();
    loadTabs();
  }, []);

  const loadColumns = () => {
    const stored = localStorage.getItem('heartFailureColumns');
    if (stored) {
      const parsed = JSON.parse(stored);
      setColumns(parsed.sort((a: Column, b: Column) => b.createdAt - a.createdAt));
    }
  };

  const loadTabs = () => {
    const storedAuthorTabs = localStorage.getItem('columnAuthorTabs');
    const storedCategoryTabs = localStorage.getItem('columnCategoryTabs');
    const storedTargetTabs = localStorage.getItem('columnTargetTabs');
    
    if (storedAuthorTabs) {
      setAuthorTabs(JSON.parse(storedAuthorTabs));
    }
    if (storedCategoryTabs) {
      setCategoryTabs(JSON.parse(storedCategoryTabs));
    }
    if (storedTargetTabs) {
      setTargetTabs(JSON.parse(storedTargetTabs));
    }
  };

  const handleAdminLogin = () => {
    if (password === 'cr.toranomon') {
      setIsAdmin(true);
      setPassword('');
    } else {
      alert('パスワードが正しくありません');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      
      if (allowedTypes.includes(file.type)) {
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onload = (event) => {
          setImagePreview(event.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        alert('対応していない画像形式です。JPG、PNG、GIF、WebPのみアップロード可能です。');
        e.target.value = '';
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const processColumn = (imageData?: string) => {
      if (editingColumn) {
        // 編集モード
        const updated = columns.map(col => 
          col.id === editingColumn.id 
            ? { ...col, ...formData, date: formData.date, imageUrl: imageData || col.imageUrl }
            : col
        );
        localStorage.setItem('heartFailureColumns', JSON.stringify(updated));
        setColumns(updated);
        setEditingColumn(null);
        alert('コラムを更新しました');
      } else {
        // 新規作成モード
        const newColumn: Column = {
          id: Date.now().toString(),
          date: formData.date,
          title: formData.title,
          content: formData.content,
          author: formData.author,
          category: formData.category,
          target: formData.target,
          createdAt: Date.now(),
          imageUrl: imageData
        };

        const updated = [newColumn, ...columns];
        localStorage.setItem('heartFailureColumns', JSON.stringify(updated));
        setColumns(updated);
        alert('コラムを投稿しました');
      }
      
      setFormData({ date: '', title: '', content: '', author: 'doctor', category: 'exercise', target: 'patient' });
      setSelectedImage(null);
      setImagePreview('');
    };

    if (selectedImage) {
      const reader = new FileReader();
      reader.onload = (event) => {
        processColumn(event.target?.result as string);
      };
      reader.readAsDataURL(selectedImage);
    } else {
      processColumn();
    }
  };

  const handleEdit = (column: Column) => {
    setEditingColumn(column);
    setFormData({
      date: column.date,
      title: column.title,
      content: column.content,
      author: column.author,
      category: column.category,
      target: column.target || 'patient'
    });
    if (column.imageUrl) {
      setImagePreview(column.imageUrl);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingColumn(null);
    setFormData({ date: '', title: '', content: '', author: 'doctor', category: 'exercise', target: 'patient' });
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleDelete = (id: string) => {
    if (window.confirm('このコラムを削除しますか？')) {
      const updated = columns.filter(item => item.id !== id);
      localStorage.setItem('heartFailureColumns', JSON.stringify(updated));
      setColumns(updated);
      if (selectedColumn?.id === id) {
        setSelectedColumn(null);
      }
    }
  };

  const handleAddTab = () => {
    if (!newTabName.trim()) {
      alert('タブ名を入力してください');
      return;
    }

    const newTab: TabConfig = {
      id: `custom-${Date.now()}`,
      name: newTabName,
      type: newTabType
    };

    if (newTabType === 'author') {
      const updated = [...authorTabs, newTab];
      setAuthorTabs(updated);
      localStorage.setItem('columnAuthorTabs', JSON.stringify(updated));
    } else if (newTabType === 'category') {
      const updated = [...categoryTabs, newTab];
      setCategoryTabs(updated);
      localStorage.setItem('columnCategoryTabs', JSON.stringify(updated));
    } else {
      const updated = [...targetTabs, newTab];
      setTargetTabs(updated);
      localStorage.setItem('columnTargetTabs', JSON.stringify(updated));
    }

    setNewTabName('');
    alert('タブを追加しました');
  };

  const handleDeleteTab = (tabId: string, type: 'author' | 'category' | 'target') => {
    if (!window.confirm('このタブを削除しますか？')) return;

    if (type === 'author') {
      const updated = authorTabs.filter(tab => tab.id !== tabId);
      setAuthorTabs(updated);
      localStorage.setItem('columnAuthorTabs', JSON.stringify(updated));
      if (activeAuthorTab === tabId) setActiveAuthorTab('all');
    } else if (type === 'category') {
      const updated = categoryTabs.filter(tab => tab.id !== tabId);
      setCategoryTabs(updated);
      localStorage.setItem('columnCategoryTabs', JSON.stringify(updated));
      if (activeCategoryTab === tabId) setActiveCategoryTab('all');
    } else {
      const updated = targetTabs.filter(tab => tab.id !== tabId);
      setTargetTabs(updated);
      localStorage.setItem('columnTargetTabs', JSON.stringify(updated));
      if (activeTargetTab === tabId) setActiveTargetTab('all');
    }
  };

  const getAuthorName = (authorId: string) => {
    const tab = authorTabs.find(t => t.id === authorId);
    return tab ? tab.name : authorId;
  };

  const getCategoryName = (categoryId: string) => {
    const tab = categoryTabs.find(t => t.id === categoryId);
    return tab ? tab.name : categoryId;
  };

  const getTargetName = (targetId: string) => {
    const tab = targetTabs.find(t => t.id === targetId);
    return tab ? tab.name : targetId;
  };

  const filteredColumns = columns.filter(column => {
    const authorMatch = activeAuthorTab === 'all' || column.author === activeAuthorTab;
    const categoryMatch = activeCategoryTab === 'all' || column.category === activeCategoryTab;
    const targetMatch = activeTargetTab === 'all' || column.target === activeTargetTab;
    return authorMatch && categoryMatch && targetMatch;
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">コラム</h1>
            <p className="text-lg text-gray-600">心臓リハビリテーションスタッフからのつぶやき</p>
          </div>

          {!isAdmin && (
            <div className="mb-8 text-right">
              <button
                onClick={() => setShowAdminPanel(!showAdminPanel)}
                className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                管理者ログイン
              </button>
            </div>
          )}

          {showAdminPanel && !isAdmin && (
            <div className="mb-12 bg-gray-50 rounded-lg p-6 max-w-md mx-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">管理者ログイン</h3>
              <div className="flex gap-3">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワードを入力"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                />
                <button
                  onClick={handleAdminLogin}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                >
                  ログイン
                </button>
              </div>
            </div>
          )}

          {isAdmin && (
            <>
              <div className="mb-12 bg-teal-50 rounded-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingColumn ? 'コラムを編集' : '新しいコラムを投稿'}
                  </h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowTabManager(!showTabManager)}
                      className="text-sm text-teal-600 hover:text-teal-800 cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-settings-3-line"></i>
                      タブ管理
                    </button>
                    <button
                      onClick={() => setIsAdmin(false)}
                      className="text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      ログアウト
                    </button>
                  </div>
                </div>

                {showTabManager && (
                  <div className="mb-6 p-6 bg-white rounded-lg border border-teal-200">
                    <h4 className="font-semibold text-gray-900 mb-4">タブ管理</h4>
                    
                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">作成者タブ</h5>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {authorTabs.map(tab => (
                          <div key={tab.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm">{tab.name}</span>
                            <button
                              onClick={() => handleDeleteTab(tab.id, 'author')}
                              className="text-red-500 hover:text-red-700 cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">内容タブ</h5>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {categoryTabs.map(tab => (
                          <div key={tab.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm">{tab.name}</span>
                            <button
                              onClick={() => handleDeleteTab(tab.id, 'category')}
                              className="text-red-500 hover:text-red-700 cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">対象者タブ</h5>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {targetTabs.map(tab => (
                          <div key={tab.id} className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
                            <span className="text-sm">{tab.name}</span>
                            <button
                              onClick={() => handleDeleteTab(tab.id, 'target')}
                              className="text-red-500 hover:text-red-700 cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">新しいタブを追加</h5>
                      <div className="flex gap-3">
                        <select
                          value={newTabType}
                          onChange={(e) => setNewTabType(e.target.value as 'author' | 'category' | 'target')}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        >
                          <option value="author">作成者</option>
                          <option value="category">内容</option>
                          <option value="target">対象者</option>
                        </select>
                        <input
                          type="text"
                          value={newTabName}
                          onChange={(e) => setNewTabName(e.target.value)}
                          placeholder="タブ名を入力"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        />
                        <button
                          onClick={handleAddTab}
                          className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap text-sm cursor-pointer"
                        >
                          追加
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        日付
                      </label>
                      <input
                        type="text"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        placeholder="例：2024年12月"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        作成者
                      </label>
                      <select
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        {authorTabs.map(tab => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        内容カテゴリ
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        {categoryTabs.map(tab => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        対象者
                      </label>
                      <select
                        value={formData.target}
                        onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        {targetTabs.map(tab => (
                          <option key={tab.id} value={tab.id}>{tab.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      タイトル
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="コラムのタイトルを入力"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      画像（任意）
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageSelect}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm cursor-pointer"
                    />
                    {imagePreview && (
                      <div className="mt-3">
                        <img
                          src={imagePreview}
                          alt="プレビュー"
                          className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                        />
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      対応形式：JPG、PNG、GIF、WebP
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      本文
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="コラムの内容を入力してください"
                      required
                      rows={8}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm resize-none"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                    >
                      {editingColumn ? '更新する' : '投稿する'}
                    </button>
                    {editingColumn && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        キャンセル
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </>
          )}

          {/* フィルタータブ */}
          <div className="mb-8">
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">作成者で絞り込み</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveAuthorTab('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeAuthorTab === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  すべて
                </button>
                {authorTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveAuthorTab(tab.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeAuthorTab === tab.id
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">内容で絞り込み</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveCategoryTab('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategoryTab === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  すべて
                </button>
                {categoryTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveCategoryTab(tab.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategoryTab === tab.id
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">対象者で絞り込み</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveTargetTab('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTargetTab === 'all'
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  すべて
                </button>
                {targetTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTargetTab(tab.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeTargetTab === tab.id
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* コラム一覧 */}
          {filteredColumns.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-article-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">
                {columns.length === 0 ? 'まだコラムはありません' : '該当するコラムがありません'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredColumns.map((column) => (
                <div
                  key={column.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {column.imageUrl && (
                    <div className="w-full h-48 overflow-hidden">
                      <img
                        src={column.imageUrl}
                        alt={column.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                            {getAuthorName(column.author)}
                          </span>
                          <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                            {getCategoryName(column.category)}
                          </span>
                          <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full whitespace-nowrap">
                            {getTargetName(column.target || 'patient')}
                          </span>
                        </div>
                        <span className="text-sm text-gray-600">{column.date}</span>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(column)}
                            className="text-teal-600 hover:text-teal-700 cursor-pointer p-2"
                          >
                            <i className="ri-edit-line text-xl"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(column.id)}
                            className="text-red-500 hover:text-red-700 cursor-pointer p-2"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>
                        </div>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {column.title}
                    </h3>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {column.content}
                    </p>

                    <button
                      onClick={() => setSelectedColumn(column)}
                      className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
                    >
                      続きを読む
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* コラム詳細モーダル */}
      {selectedColumn && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
          onClick={() => setSelectedColumn(null)}
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-start">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {getAuthorName(selectedColumn.author)}
                  </span>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {getCategoryName(selectedColumn.category)}
                  </span>
                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {getTargetName(selectedColumn.target || 'patient')}
                  </span>
                  <span className="text-sm text-gray-600">{selectedColumn.date}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedColumn.title}
                </h2>
              </div>
              <button
                onClick={() => setSelectedColumn(null)}
                className="text-gray-400 hover:text-gray-600 cursor-pointer p-2"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="px-8 py-6">
              {selectedColumn.imageUrl && (
                <div className="mb-6">
                  <img
                    src={selectedColumn.imageUrl}
                    alt={selectedColumn.title}
                    className="w-full rounded-lg"
                  />
                </div>
              )}
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedColumn.content}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
